import { 
  Decision, 
  Evidence, 
  ValidationError, 
  DECISION_TYPES, 
  INFORMATION_SOURCES,
  FILE_SIZE_LIMITS,
  SUPPORTED_FILE_TYPES,
  MIME_TYPES 
} from '../../types/audit-trail';

export class ValidationService {
  
  /**
   * Validate decision form data
   */
  validateDecision(decision: Partial<Decision>): ValidationError[] {
    const errors: ValidationError[] = [];

    // Required field validations
    if (!decision.title?.trim()) {
      errors.push({
        field: 'title',
        message: 'Decision title is required'
      });
    } else if (decision.title.length < 5) {
      errors.push({
        field: 'title',
        message: 'Decision title must be at least 5 characters'
      });
    } else if (decision.title.length > 200) {
      errors.push({
        field: 'title',
        message: 'Decision title must be less than 200 characters'
      });
    }

    if (!decision.type) {
      errors.push({
        field: 'type',
        message: 'Decision type is required'
      });
    } else if (!Object.keys(DECISION_TYPES).includes(decision.type)) {
      errors.push({
        field: 'type',
        message: 'Invalid decision type'
      });
    }

    if (!decision.decisionMade?.trim()) {
      errors.push({
        field: 'decisionMade',
        message: 'Decision details are required'
      });
    } else if (decision.decisionMade.length < 10) {
      errors.push({
        field: 'decisionMade',
        message: 'Decision details must be at least 10 characters'
      });
    } else if (decision.decisionMade.length > 2000) {
      errors.push({
        field: 'decisionMade',
        message: 'Decision details must be less than 2000 characters'
      });
    }

    if (!decision.rationale?.trim()) {
      errors.push({
        field: 'rationale',
        message: 'Decision rationale is required for audit purposes'
      });
    } else if (decision.rationale.length < 20) {
      errors.push({
        field: 'rationale',
        message: 'Rationale must be at least 20 characters for proper documentation'
      });
    } else if (decision.rationale.length > 3000) {
      errors.push({
        field: 'rationale',
        message: 'Rationale must be less than 3000 characters'
      });
    }

    if (!decision.riskLevel) {
      errors.push({
        field: 'riskLevel',
        message: 'Risk assessment is required'
      });
    } else if (!['low', 'medium', 'high'].includes(decision.riskLevel)) {
      errors.push({
        field: 'riskLevel',
        message: 'Invalid risk level'
      });
    }

    // Information sources validation
    if (!decision.informationSources || decision.informationSources.length === 0) {
      errors.push({
        field: 'informationSources',
        message: 'At least one information source must be selected'
      });
    } else {
      const invalidSources = decision.informationSources.filter(
        source => !INFORMATION_SOURCES.includes(source)
      );
      if (invalidSources.length > 0) {
        errors.push({
          field: 'informationSources',
          message: `Invalid information sources: ${invalidSources.join(', ')}`
        });
      }
    }

    // Stakeholders validation
    if (!decision.stakeholders || decision.stakeholders.length === 0) {
      errors.push({
        field: 'stakeholders',
        message: 'At least one stakeholder must be consulted and documented'
      });
    } else {
      // Validate stakeholder format (should not be empty strings)
      const invalidStakeholders = decision.stakeholders.filter(
        stakeholder => !stakeholder.trim()
      );
      if (invalidStakeholders.length > 0) {
        errors.push({
          field: 'stakeholders',
          message: 'Stakeholder names cannot be empty'
        });
      }

      // Check for reasonable stakeholder names
      decision.stakeholders.forEach((stakeholder, index) => {
        if (stakeholder.length < 2) {
          errors.push({
            field: 'stakeholders',
            message: `Stakeholder ${index + 1} name is too short`
          });
        }
        if (stakeholder.length > 100) {
          errors.push({
            field: 'stakeholders',
            message: `Stakeholder ${index + 1} name is too long`
          });
        }
      });
    }

    // Alternatives validation (optional but if provided, should be meaningful)
    if (decision.alternatives && decision.alternatives.trim()) {
      if (decision.alternatives.length < 10) {
        errors.push({
          field: 'alternatives',
          message: 'If alternatives are provided, they should be detailed (minimum 10 characters)'
        });
      }
      if (decision.alternatives.length > 2000) {
        errors.push({
          field: 'alternatives',
          message: 'Alternatives description is too long (maximum 2000 characters)'
        });
      }
    }

    // Location validation (if provided)
    if (decision.location) {
      if (
        typeof decision.location.lat !== 'number' || 
        typeof decision.location.lng !== 'number'
      ) {
        errors.push({
          field: 'location',
          message: 'Invalid location coordinates'
        });
      } else {
        if (decision.location.lat < -90 || decision.location.lat > 90) {
          errors.push({
            field: 'location',
            message: 'Latitude must be between -90 and 90 degrees'
          });
        }
        if (decision.location.lng < -180 || decision.location.lng > 180) {
          errors.push({
            field: 'location',
            message: 'Longitude must be between -180 and 180 degrees'
          });
        }
      }
    }

    // Tags validation (if provided)
    if (decision.tags) {
      decision.tags.forEach((tag, index) => {
        if (!tag.trim()) {
          errors.push({
            field: 'tags',
            message: `Tag ${index + 1} cannot be empty`
          });
        }
        if (tag.length > 50) {
          errors.push({
            field: 'tags',
            message: `Tag ${index + 1} is too long (maximum 50 characters)`
          });
        }
      });
    }

    // High-risk decision additional validation
    if (decision.riskLevel === 'high') {
      if (!decision.rationale || decision.rationale.length < 50) {
        errors.push({
          field: 'rationale',
          message: 'High-risk decisions require detailed rationale (minimum 50 characters)'
        });
      }
      
      if (!decision.stakeholders || decision.stakeholders.length < 2) {
        errors.push({
          field: 'stakeholders',
          message: 'High-risk decisions should involve multiple stakeholders'
        });
      }

      if (!decision.alternatives || decision.alternatives.length < 20) {
        errors.push({
          field: 'alternatives',
          message: 'High-risk decisions require documentation of alternatives considered'
        });
      }
    }

    return errors;
  }

  /**
   * Validate evidence file
   */
  validateEvidenceFile(file: File): ValidationError[] {
    const errors: ValidationError[] = [];

    // File size validation
    if (file.size > FILE_SIZE_LIMITS.maxFileSize) {
      errors.push({
        field: 'evidence',
        message: `File "${file.name}" is too large (max ${FILE_SIZE_LIMITS.maxFileSize / 1024 / 1024}MB)`
      });
    }

    // File type validation
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    const isSupported = Object.values(SUPPORTED_FILE_TYPES).flat().includes(fileExtension);
    
    if (!isSupported) {
      errors.push({
        field: 'evidence',
        message: `File type "${fileExtension}" is not supported`
      });
    }

    // MIME type validation
    if (!Object.keys(MIME_TYPES).includes(file.type)) {
      errors.push({
        field: 'evidence',
        message: `MIME type "${file.type}" is not allowed`
      });
    }

    // Filename validation
    if (file.name.length > 255) {
      errors.push({
        field: 'evidence',
        message: 'Filename is too long (max 255 characters)'
      });
    }

    // Check for potentially dangerous filenames
    const dangerousPatterns = ['.exe', '.bat', '.cmd', '.scr', '.vbs', '.js'];
    if (dangerousPatterns.some(pattern => file.name.toLowerCase().includes(pattern))) {
      errors.push({
        field: 'evidence',
        message: 'File type may not be safe to upload'
      });
    }

    return errors;
  }

  /**
   * Validate total evidence size for a decision
   */
  validateTotalEvidenceSize(evidence: Evidence[], newFile?: File): ValidationError[] {
    const errors: ValidationError[] = [];
    
    const currentSize = evidence.reduce((total, item) => total + item.size, 0);
    const newFileSize = newFile ? newFile.size : 0;
    const totalSize = currentSize + newFileSize;

    if (totalSize > FILE_SIZE_LIMITS.maxTotalSize) {
      errors.push({
        field: 'evidence',
        message: `Total evidence size exceeds limit (max ${FILE_SIZE_LIMITS.maxTotalSize / 1024 / 1024}MB)`
      });
    }

    return errors;
  }

  /**
   * Validate audio duration
   */
  async validateAudioDuration(file: File): Promise<ValidationError[]> {
    const errors: ValidationError[] = [];

    if (!file.type.startsWith('audio/')) {
      return errors;
    }

    try {
      const duration = await this.getAudioDuration(file);
      if (duration > FILE_SIZE_LIMITS.maxAudioDuration) {
        errors.push({
          field: 'evidence',
          message: `Audio file "${file.name}" is too long (max ${FILE_SIZE_LIMITS.maxAudioDuration / 60} minutes)`
        });
      }
    } catch (error) {
      errors.push({
        field: 'evidence',
        message: `Cannot validate audio duration for "${file.name}"`
      });
    }

    return errors;
  }

  /**
   * Get audio file duration
   */
  private getAudioDuration(file: File): Promise<number> {
    return new Promise((resolve, reject) => {
      const audio = document.createElement('audio');
      const url = URL.createObjectURL(file);
      
      audio.addEventListener('loadedmetadata', () => {
        URL.revokeObjectURL(url);
        resolve(audio.duration);
      });
      
      audio.addEventListener('error', () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load audio metadata'));
      });
      
      audio.src = url;
    });
  }

  /**
   * Validate evidence metadata
   */
  validateEvidence(evidence: Evidence): ValidationError[] {
    const errors: ValidationError[] = [];

    if (!evidence.id) {
      errors.push({
        field: 'evidence',
        message: 'Evidence ID is required'
      });
    }

    if (!evidence.filename) {
      errors.push({
        field: 'evidence',
        message: 'Evidence filename is required'
      });
    }

    if (!evidence.type) {
      errors.push({
        field: 'evidence',
        message: 'Evidence type is required'
      });
    } else if (!['photo', 'document', 'audio', 'screenshot'].includes(evidence.type)) {
      errors.push({
        field: 'evidence',
        message: 'Invalid evidence type'
      });
    }

    if (typeof evidence.size !== 'number' || evidence.size <= 0) {
      errors.push({
        field: 'evidence',
        message: 'Invalid evidence file size'
      });
    }

    return errors;
  }

  /**
   * Validate form completeness for different risk levels
   */
  validateFormCompleteness(decision: Partial<Decision>): {
    isComplete: boolean;
    missingFields: string[];
    recommendations: string[];
  } {
    const missingFields: string[] = [];
    const recommendations: string[] = [];

    // Always required fields
    if (!decision.title) missingFields.push('Decision Title');
    if (!decision.type) missingFields.push('Decision Type');
    if (!decision.decisionMade) missingFields.push('Decision Made');
    if (!decision.rationale) missingFields.push('Rationale');
    if (!decision.riskLevel) missingFields.push('Risk Assessment');
    if (!decision.informationSources?.length) missingFields.push('Information Sources');
    if (!decision.stakeholders?.length) missingFields.push('Stakeholders Consulted');

    // Risk-level specific requirements
    if (decision.riskLevel === 'high') {
      if (!decision.alternatives) missingFields.push('Alternatives Considered');
      if (!decision.location) recommendations.push('Consider adding location for high-risk decisions');
      if (!decision.evidence?.length) recommendations.push('Consider adding evidence for high-risk decisions');
    }

    if (decision.riskLevel === 'medium' || decision.riskLevel === 'high') {
      if (!decision.alternatives) recommendations.push('Consider documenting alternatives for better audit trail');
    }

    // General recommendations
    if (!decision.location) recommendations.push('Adding location helps with context');
    if (!decision.evidence?.length) recommendations.push('Evidence files strengthen the audit trail');
    if (!decision.tags?.length) recommendations.push('Tags help with organization and search');

    return {
      isComplete: missingFields.length === 0,
      missingFields,
      recommendations
    };
  }

  /**
   * Validate decision before submission
   */
  validateForSubmission(decision: Decision): ValidationError[] {
    const errors: ValidationError[] = [];

    // All standard validations
    errors.push(...this.validateDecision(decision));

    // Evidence validations
    if (decision.evidence) {
      decision.evidence.forEach(evidence => {
        errors.push(...this.validateEvidence(evidence));
      });
    }

    // Submission-specific validations
    if (!decision.createdBy) {
      errors.push({
        field: 'createdBy',
        message: 'Decision creator must be identified'
      });
    }

    if (!decision.signature) {
      errors.push({
        field: 'signature',
        message: 'Decision signature is required for audit trail'
      });
    }

    if (!decision.deviceId) {
      errors.push({
        field: 'deviceId',
        message: 'Device identification is required for security'
      });
    }

    return errors;
  }

  /**
   * Get validation summary
   */
  getValidationSummary(decision: Partial<Decision>): {
    isValid: boolean;
    errorCount: number;
    warningCount: number;
    errors: ValidationError[];
    completeness: {
      isComplete: boolean;
      missingFields: string[];
      recommendations: string[];
    };
  } {
    const errors = this.validateDecision(decision);
    const completeness = this.validateFormCompleteness(decision);

    return {
      isValid: errors.length === 0 && completeness.isComplete,
      errorCount: errors.length,
      warningCount: completeness.missingFields.length + completeness.recommendations.length,
      errors,
      completeness
    };
  }
}

// Create singleton instance
export const validationService = new ValidationService();
export default ValidationService;